import React, { useState } from 'react';
import { Box, TextField, Button, Stack, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Lock } from '@mui/icons-material';
import axios from 'axios';

const PasswordChange = ({ userId, onPasswordChanged }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All fields are required.');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`/api/users/${userId}/password`, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      setSuccess(true);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      if (onPasswordChanged) onPasswordChanged();
      setTimeout(() => {
        setOpen(false);
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.response?.data?.message || 'Failed to change password. Please check your current password.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
    setSuccess(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Lock />}
        onClick={() => setOpen(true)}
        sx={{ mb: 2 }}
      >
        Change Password
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <TextField
                label="Current Password"
                type="password"
                value={formData.currentPassword}
                onChange={handleChange('currentPassword')}
                fullWidth
                required
                autoFocus
              />
              <TextField
                label="New Password"
                type="password"
                value={formData.newPassword}
                onChange={handleChange('newPassword')}
                fullWidth
                required
                helperText="Must be at least 6 characters long"
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                fullWidth
                required
              />
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">Password changed successfully!</Alert>}
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || success}
          >
            {submitting ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PasswordChange;

